import { useState, useEffect } from "react"
import { Button, FieldError, Input, Label, Tab, TabList, TabPanel, Tabs, TextArea, TextField } from "react-aria-components"
import { Intent, IPartCase, IRecallItem } from "../recallWizard/recallData"
import { modalSignal } from "./ModalContainer"
import iconCross from "../../../assets/icons/icon-cross.svg"
import { getStatus } from "../../../enums/Domain/StatusLookup"
import LoadingButton from "../LoadingButton"
import { httpPost } from "../../../modules/Backend"
import { HttpStatusCode } from "axios"

interface IAddToRecallModal {
    item: IRecallItem
    addFunc: Function
}

export default function AddToRecallModal({ item, addFunc }: IAddToRecallModal) {
    const [partCases, setPartcases] = useState<IPartCase[][]>(item.partCases ?? [])
    const [intent, setIntent] = useState<Intent>(item.intent)
    const [error, setError] = useState<string>()
    const [value, setValue] = useState<string>()
    const [loading, setLoading] = useState<boolean>(false)
    const [parsing, setParsing] = useState<boolean>(false)
    const [requestorComments, setRequestorComments] = useState<string>(item.requestorComments ?? "")

    useEffect(() => {
        if (item.requestorComments !== undefined) {
            setRequestorComments(item.requestorComments ?? "");
        }
        if (item.partCases !== undefined) {
            setPartcases(item.partCases ?? []);
        }
        if (item.intent !== undefined) {
            setIntent(item.intent);
        }
    }, [item.requestorComments, item.partCases, item.intent]);

    async function parseSpecimenInput(input: string) {
        if (!input || !input.trim()) {
            setError("Please enter at least one part case")
            return
        }

        setParsing(true)
        setError("")

        try {
            // Convert existing part cases to the format expected by the API
            const existingPartCasesForApi = partCases.map(group =>
                group.map(pc => ({ reference: pc.reference }))
            )

            const result = await httpPost("api/specimens/parse", { 
                input: input.trim(),
                existingPartCases: existingPartCasesForApi
            }, null, undefined, true)

            if (result.status === HttpStatusCode.Ok && result.data?.partCases) {
                const partCasesToAdd = result.data.partCases.map((group: any[]) =>
                    group.map((pc: any) => ({ reference: pc.reference } as IPartCase))
                ) as IPartCase[][]

                if (partCasesToAdd.length === 0) {
                    setError("Please enter at least one part case")
                    setParsing(false)
                    return
                }

                setPartcases(prev => prev.concat(partCasesToAdd))
                setValue("")
                setError("")
            } else {
                setError("Failed to add part cases. Part cases may already be selected or contain duplicates.")
            }
        } catch (error) {
            setError("Failed to add part cases. Please try again.")
        } finally {
            setParsing(false)
        }
    }

    function removePartCase(index: number) {
        if (index > -1 && index < partCases.length) {
            const newPartCases = [...partCases]
            newPartCases.splice(index, 1)
            setPartcases(newPartCases);
        }
    }

    function isDisabled() {
        if (intent === Intent.PartCase && partCases.length === 0 || loading) {
            return true;
        }
        return loading;
    }

    async function submit() {
        setLoading(true);

        const updatedItem: IRecallItem = { 
            ...item, 
            intent: intent, 
            partCases: intent === Intent.PartCase ? partCases : [],
            requestorComments: requestorComments ?? ""
        };
        
        // Ensure requestorComments is always included
        if (updatedItem.requestorComments === undefined) {
            updatedItem.requestorComments = "";
        }
        
        const invalidPartCases = await addFunc(updatedItem);

        setLoading(false);

        if (invalidPartCases && invalidPartCases.length > 0) {
            setError(`Part case ${invalidPartCases[0]} is invalid`)
        }
        else {
            modalSignal.value = undefined
        }
    }

    function getInventoryQuestion(item: IRecallItem) {
        if (item.canBeRecalledInWholeBoxes) {
            return 'Are you recalling a whole case, part case or whole box?';
        }
        return 'Are you recalling a whole case or part case?'
    }

    return (
        <div className="recall-wizard-add-modal__layout display--flex flex__direction--column gap--s">
            <div className="modal__body display--flex flex__direction--column gap--s">
                <h2>Choose inventory to recall</h2>
                {Information(item)}
                <Tabs selectedKey={intent} className={"recall-wizard-add-modal__tabs"} onSelectionChange={(x) => { setIntent(x as Intent) }} >
                    <div className="grid-x grid-margin-x margin__top--s recall-wizard-add-modal__header-section">
                        <div className="small-7">
                            <p className="small">{getInventoryQuestion(item)}</p>
                            <TabList className={"recall-wizard-add-modal__tabs__wrapper margin__top--xxs"} >
                                <Tab className={"recall-wizard-add-modal__tabs__tab"} id={Intent.WholeCase}><p className="button-text button-text--small">{"Whole case"}</p></Tab>
                                <Tab className={"recall-wizard-add-modal__tabs__tab"} id={Intent.PartCase}><p className="button-text button-text--small">{"Part case"}</p></Tab>
                                {item.canBeRecalledInWholeBoxes &&
                                    <Tab className={"recall-wizard-add-modal__tabs__tab"} id={Intent.WholeBox}><p className="button-text button-text--small">{"Whole Box"}</p></Tab>
                                }
                            </TabList>
                        </div>
                        <div className="small-5">
                            <p className="small margin__bottom--xxxs">Additional Comments</p>
                            <TextArea 
                                value={requestorComments} 
                                placeholder="Please do not enter part case information here"
                                onChange={(e) => setRequestorComments(e.target.value)} 
                                aria-label="Comments"
                                className="recall-wizard-add-modal__comments margin__top--xxs"
                                rows={3}
                            />
                        </div>
                    </div>
                    <TabPanel id={Intent.WholeCase}>
                        <div className="recall-wizard-add-modal__divider"></div>
                    </TabPanel>
                    <TabPanel id={Intent.PartCase}>
                        <TextField value={value} onChange={(val) => { setValue(val) }} type={"text"} className={"recall-wizard-add-modal__search-field "} aria-label={"Required part cases"} isInvalid={!!error}>
                            <Label className="small margin__bottom--xxxs display--block">Required part cases</Label>
                            <div className="recall-wizard-add-modal__add-part-cases">
                                <Input />
                                <LoadingButton
                                    className="button button--large button--primary button--with-icon"
                                    onPress={() => parseSpecimenInput(value ?? "")}
                                    buttonText="Add"
                                    loadingText=""
                                    isDisabled={parsing || !value || !value.trim()}
                                    isLoading={parsing}
                                />
                            </div>
                            <FieldError>{error}</FieldError>
                            <p className="xsmall">Enter part cases in any format. Spans like A2-A20 will be expanded automatically. Multiple part cases can be comma-separated.</p>
                        </TextField>
                        <div className="recall-wizard-add-modal__list">
                            {partCases.map((partCase, i) => (
                                <div className="item" key={i}>
                                    <p className="small">{partCase[0].reference}{partCase.length > 1 ? ` - ${partCase[partCase.length - 1].reference}` : ""} <span className="small--2">({partCase.length} {partCase.length === 1 ? 'part case' : 'part cases'})</span></p>
                                    <Button className={"cursor__pointer"} onPress={() => removePartCase(i)}>
                                        <img src={iconCross} alt="" />
                                    </Button>
                                </div>
                            ))}
                        </div>
                    </TabPanel>
                    {item.canBeRecalledInWholeBoxes &&
                        <TabPanel id={Intent.WholeBox}>
                            <div className="recall-wizard-add-modal__divider"></div>
                        </TabPanel>
                    }
                </Tabs>
            </div>
            <div className="modal__footer display--flex gap--s justifyContent--flex-end">
                <Button className=" button button--large button--secondary button--with-icon" onPress={() => { modalSignal.value = undefined }}>
                    <p className="button-text button-text--small">Cancel</p>
                </Button>
                <LoadingButton className=" button button--large button--primary button--with-icon"
                    onPress={submit}
                    buttonText="Save"
                    loadingText="Saving"
                    isDisabled={isDisabled()}
                    isLoading={loading}
                >
                </LoadingButton>
            </div>
        </div>
    )
}

function Information(item: IRecallItem) {
    return (
        <div className="recall-wizard-add-modal__information grid-x">
            <div className="small-3">
                <p className="small colour-secondary">Type</p>
                <p className="small small--2 textWrap">{item.materialType.name}</p>
            </div>
            <div className="small-3">
                <p className="small colour-secondary">Case number</p>
                <div className="display--flex alignItems--center gap--xxs">
                    <p className="small small--2 textWrap">{item.reference}</p>
                </div>
            </div>
            <div className="small-3">
                <p className="small colour-secondary">Year</p>
                <p className="small small--2 textWrap">{item.year}</p>
            </div>
            <div className="small-3">
                <p className="small colour-secondary">Status</p>
                <p className="small small--2 textWrap">{getStatus(item.status)?.displayValue}</p>
            </div>
        </div>
    )
}